"use client";

import { useCallback, useEffect, useReducer, useRef } from "react";
import { AlertCircle, X } from "@/components/icons";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { TooltipIconButton } from "@/components/ui/tooltip-icon-button";
import {
  GLOBAL_ERROR_EVENT,
  type GlobalErrorDetail,
  isGlobalErrorDetail,
} from "@/lib/feedback/global-error-event";
import { openReportIssueDialog } from "@/lib/feedback/report-issue-event";
import { eventTargetAsNode } from "@/lib/utils/event-target";

const BANNER_DISMISS_MS = 10_000;
const QUEUE_MAX = 5;

interface BannerState {
  current: GlobalErrorDetail | null;
  queue: Array<GlobalErrorDetail>;
}

type BannerAction =
  | { type: "enqueue"; detail: GlobalErrorDetail }
  | { type: "advance" };

const INITIAL_STATE: BannerState = { current: null, queue: [] };

function bannerReducer(state: BannerState, action: BannerAction): BannerState {
  switch (action.type) {
    case "enqueue": {
      const { detail } = action;
      if (state.current && state.current.message === detail.message) {
        return state;
      }
      if (!state.current) {
        return { current: detail, queue: state.queue };
      }
      if (state.queue.some((q) => q.message === detail.message)) {
        return state;
      }
      const nextQueue =
        state.queue.length >= QUEUE_MAX
          ? state.queue.slice(1)
          : state.queue.slice();
      nextQueue.push(detail);
      return { current: state.current, queue: nextQueue };
    }
    case "advance": {
      const [next = null, ...rest] = state.queue;
      return { current: next, queue: rest };
    }
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}

export function GlobalErrorBanner() {
  const [{ current, queue }, dispatch] = useReducer(
    bannerReducer,
    INITIAL_STATE,
  );
  const isPausedRef = useRef(false);

  const advance = useCallback(() => {
    dispatch({ type: "advance" });
  }, []);

  const dismiss = advance;

  useEffect(() => {
    function handler(event: Event) {
      if (!(event instanceof CustomEvent)) return;
      const detail: unknown = event.detail;
      if (!isGlobalErrorDetail(detail) || !detail.message) return;
      dispatch({ type: "enqueue", detail });
    }
    window.addEventListener(GLOBAL_ERROR_EVENT, handler);
    return () => window.removeEventListener(GLOBAL_ERROR_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!current) return;
    let timerId: number | null = null;
    function schedule() {
      timerId = window.setTimeout(() => {
        if (isPausedRef.current) {
          schedule();
          return;
        }
        advance();
      }, BANNER_DISMISS_MS);
    }
    schedule();
    return () => {
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [current, advance]);

  if (!current) return null;

  const canReport = current.reportable ?? true;
  const queueSize = queue.length;

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      onMouseEnter={() => {
        isPausedRef.current = true;
      }}
      onMouseLeave={() => {
        isPausedRef.current = false;
      }}
      onFocusCapture={() => {
        isPausedRef.current = true;
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(eventTargetAsNode(event.relatedTarget))) {
          isPausedRef.current = false;
        }
      }}
      className={cn(
        "fixed left-1/2 top-4 z-[60] w-[min(560px,calc(100vw-2rem))] -translate-x-1/2",
        "rounded-md border border-border bg-popover text-popover-foreground shadow-lg",
        "animate-in fade-in slide-in-from-top-2 duration-150",
        "pt-[max(0px,env(safe-area-inset-top))]",
      )}
    >
      <div className="flex items-start gap-2.5 py-2.5 pl-3 pr-2">
        <AlertCircle
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-destructive-strong"
        />
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1 text-sm leading-5">
          <p>{current.message}</p>
          {queueSize > 0 && (
            <p className="text-xs text-muted-foreground">
              {queueSize} more {queueSize === 1 ? "message" : "messages"}
            </p>
          )}
          {canReport && (
            <Button
              type="button"
              size="bare-xs"
              variant="link"
              onClick={() => {
                openReportIssueDialog({
                  errorMessage: current.message,
                  ...current.reportContext,
                });
                dismiss();
              }}
            >
              Report problem
            </Button>
          )}
        </div>
        <TooltipIconButton
          label="Dismiss"
          variant="quiet"
          size="icon-compact"
          type="button"
          onClick={dismiss}
          className="-my-0.5 shrink-0"
        >
          <X className="size-4" />
        </TooltipIconButton>
      </div>
    </div>
  );
}
